// Constants for scoring matches
const SCORE_CONTINUE_MATCH = 1
const SCORE_SPACE_WORD_JUMP = 0.9
const SCORE_NON_SPACE_WORD_JUMP = 0.8
const SCORE_CHARACTER_JUMP = 0.17
const SCORE_TRANSPOSITION = 0.1
const PENALTY_SKIPPED = 0.999
const PENALTY_CASE_MISMATCH = 0.9999
const PENALTY_NOT_COMPLETE = 0.99

// Regular expressions for identifying word breaks
// eslint-disable-next-line no-useless-escape
const IS_GAP_REGEXP = /[\\\/_+.#"@\[\(\{&]/
// eslint-disable-next-line no-useless-escape
const COUNT_GAPS_REGEXP = /[\\\/_+.#"@\[\(\{&]/g

const IS_SPACE_REGEXP = /[\s-]/
const COUNT_SPACE_REGEXP = /[\s-]/g

// Fuzzy sort algorithm for matching search terms.
// Taken from the cmdk package that powers the Command component from shadcn
// https://github.com/pacocoursey/cmdk/blob/main/cmdk/src/command-score.ts

/**
 * Recursively calculates the score for a string and abbreviation match
 */
function commandScoreInner(
  string: string,
  abbreviation: string,
  lowerString: string,
  lowerAbbreviation: string,
  stringIndex: number,
  abbreviationIndex: number,
  memoizedResults: Record<string, number>
): number {
  // If we've reached the end of the abbreviation, we have a match
  if (abbreviationIndex === abbreviation.length) {
    if (stringIndex === string.length) {
      return SCORE_CONTINUE_MATCH
    }
    return PENALTY_NOT_COMPLETE
  }

  // Check if we've already calculated this result
  const memoizeKey = `${stringIndex},${abbreviationIndex}`
  if (memoizedResults[memoizeKey] !== undefined) {
    return memoizedResults[memoizeKey]
  }

  const abbreviationChar = lowerAbbreviation.charAt(abbreviationIndex)
  let index = lowerString.indexOf(abbreviationChar, stringIndex)
  let highScore = 0

  let score, transposedScore, wordBreaks, spaceBreaks

  // Try all possible matches of the current abbreviation character
  while (index >= 0) {
    // Calculate score for the rest of the string
    score = commandScoreInner(
      string,
      abbreviation,
      lowerString,
      lowerAbbreviation,
      index + 1,
      abbreviationIndex + 1,
      memoizedResults
    )

    if (score > highScore) {
      // Adjust score based on match quality
      if (index === stringIndex) {
        // Continuous match
        score *= SCORE_CONTINUE_MATCH
      } else if (IS_GAP_REGEXP.test(string.charAt(index - 1))) {
        // Non-space word jump (e.g., after a slash, underscore)
        score *= SCORE_NON_SPACE_WORD_JUMP
        wordBreaks = string
          .slice(stringIndex, index - 1)
          .match(COUNT_GAPS_REGEXP)
        if (wordBreaks && stringIndex > 0) {
          score *= Math.pow(PENALTY_SKIPPED, wordBreaks.length)
        }
      } else if (IS_SPACE_REGEXP.test(string.charAt(index - 1))) {
        // Space word jump
        score *= SCORE_SPACE_WORD_JUMP
        spaceBreaks = string
          .slice(stringIndex, index - 1)
          .match(COUNT_SPACE_REGEXP)
        if (spaceBreaks && stringIndex > 0) {
          score *= Math.pow(PENALTY_SKIPPED, spaceBreaks.length)
        }
      } else {
        // Character jump
        score *= SCORE_CHARACTER_JUMP
        if (stringIndex > 0) {
          score *= Math.pow(PENALTY_SKIPPED, index - stringIndex)
        }
      }

      // Penalize case mismatches
      if (string.charAt(index) !== abbreviation.charAt(abbreviationIndex)) {
        score *= PENALTY_CASE_MISMATCH
      }
    }

    // Check for transpositions
    if (
      (score < SCORE_TRANSPOSITION &&
        lowerString.charAt(index - 1) ===
          lowerAbbreviation.charAt(abbreviationIndex + 1)) ||
      (lowerAbbreviation.charAt(abbreviationIndex + 1) ===
        lowerAbbreviation.charAt(abbreviationIndex) && // allow duplicate letters
        lowerString.charAt(index - 1) !==
          lowerAbbreviation.charAt(abbreviationIndex))
    ) {
      transposedScore = commandScoreInner(
        string,
        abbreviation,
        lowerString,
        lowerAbbreviation,
        index + 1,
        abbreviationIndex + 2,
        memoizedResults
      )

      if (transposedScore * SCORE_TRANSPOSITION > score) {
        score = transposedScore * SCORE_TRANSPOSITION
      }
    }

    if (score > highScore) {
      highScore = score
    }

    // Find next occurrence of the current abbreviation character
    index = lowerString.indexOf(abbreviationChar, index + 1)
  }

  // Memoize the result
  memoizedResults[memoizeKey] = highScore
  return highScore
}

/**
 * Normalizes input for consistent matching
 */
function formatInput(string: string): string {
  // Convert all valid space characters to space so they match each other
  return string.toLowerCase().replace(COUNT_SPACE_REGEXP, ' ')
}

/**
 * Calculates a score for how well the abbreviation matches the string
 * Higher scores indicate better matches
 *
 * @param string The string to match against
 * @param abbreviation The search term/abbreviation to match
 * @param aliases Optional additional strings to match
 * @returns A score between 0 and 1, where 1 is a perfect match
 */
export function commandScore(
  string: string,
  abbreviation: string,
  aliases: string[] = []
): number {
  // Add aliases to the string if provided
  string =
    aliases && aliases.length > 0
      ? `${string + ' ' + aliases.join(' ')}`
      : string

  // Pre-format strings for better performance
  const formattedString = formatInput(string)
  const formattedAbbreviation = formatInput(abbreviation)

  return commandScoreInner(
    string,
    abbreviation,
    formattedString,
    formattedAbbreviation,
    0,
    0,
    {}
  )
}

// Example usage in React component:
//
// import { commandScore } from './commandScore';
//
// function SearchableList({ items, searchQuery }) {
//   const filteredItems = items
//     .map(item => ({
//       item,
//       score: commandScore(item.name, searchQuery, item.aliases || [])
//     }))
//     .filter(({ score }) => score > 0)
//     .sort((a, b) => b.score - a.score)
//     .map(({ item }) => item);
//
//   return (
//     <ul>
//       {filteredItems.map(item => (
//         <li key={item.id}>{item.name}</li>
//       ))}
//     </ul>
//   );
// }
